import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { CubeQuery, CubeQueryRecord, RequestTiming } from '@/dev/types/cubeQueryTypes';
import { extractComponentNameWithFallback } from '@/dev/services/componentNameExtractor';
import { isCubeQuery } from '@/dev/utils/cubeQueryGuards';
import { getResponseRowCount } from '@/dev/utils/cubeQueryRunnerUtils';

export interface InterceptorCallbacks {
  onQueryCaptured: (record: CubeQueryRecord) => void;
  getIsRecording: () => boolean;
  getMaxQueries: () => number;
  getCurrentQueriesCount: () => number;
  generateRequestId: () => string;
  extractQueryKey: (cubeQuery: CubeQuery) => string[] | undefined;
}

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;
const requestTimings = new Map<string, RequestTiming>();
const requestIds = new WeakMap<InternalAxiosRequestConfig, string>();

const isCubeRequest = (url: string): boolean => url.includes('/v1/load') || url.includes('/cubejs-api/v1');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseSerializedQuery = (value: string): CubeQuery | null => {
  try {
    const parsedQuery: unknown = JSON.parse(value);
    return isCubeQuery(parsedQuery) ? parsedQuery : null;
  } catch {}
  return null;
};

const parseQueryFromUrl = (fullUrl: string): CubeQuery | null => {
  try {
    const parsedUrl = new URL(fullUrl, window.location.origin);
    const queryParam = parsedUrl.searchParams.get('query');
    return queryParam ? parseSerializedQuery(queryParam) : null;
  } catch {
    return null;
  }
};

const parseQueryFromData = (data: unknown): CubeQuery | null => {
  if (typeof data === 'string') return parseSerializedQuery(data);
  if (!isRecord(data) || !('query' in data)) return isCubeQuery(data) ? data : null;

  const query = data.query;
  if (typeof query === 'string') return parseSerializedQuery(query);
  return isCubeQuery(query) ? query : null;
};

const getFullUrl = (config: InternalAxiosRequestConfig): string => {
  const url = config.url || '';
  return config.baseURL ? `${config.baseURL}${url}` : url;
};

const getHttpMethod = (method: string | undefined): 'GET' | 'POST' =>
  method?.toUpperCase() === 'POST' ? 'POST' : 'GET';

export const setupAxiosInterceptor = (callbacks: InterceptorCallbacks): void => {
  if (requestInterceptorId !== null) {
    return;
  }

  requestInterceptorId = axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!callbacks.getIsRecording()) return config;

      const fullUrl = getFullUrl(config);

      if (isCubeRequest(fullUrl)) {
        const requestId = callbacks.generateRequestId();
        const query = parseQueryFromUrl(fullUrl) ?? parseQueryFromData(config.data);
        if (!query) return config;
        const queryKey = callbacks.extractQueryKey(query);
        const componentName = extractComponentNameWithFallback(query, queryKey);

        requestTimings.set(requestId, {
          startTime: Date.now(),
          performanceStartTime: performance.now(),
          componentName,
          query,
          queryKey,
        });

        requestIds.set(config, requestId);
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const requestId = requestIds.get(response.config);
      if (requestId) {
        const timing = requestTimings.get(requestId);
        requestIds.delete(response.config);
        requestTimings.delete(requestId);
        if (!timing || !timing.query) return response;

        const duration = Math.round(performance.now() - timing.performanceStartTime);
        const fullUrl = getFullUrl(response.config);

        if (callbacks.getIsRecording() && callbacks.getCurrentQueriesCount() < callbacks.getMaxQueries()) {
          const record: CubeQueryRecord = {
            id: requestId,
            timestamp: timing.startTime,
            componentName: timing.componentName,
            pagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
            query: timing.query,
            queryKey: timing.queryKey || [],
            url: fullUrl,
            method: getHttpMethod(response.config.method),
            duration,
            rowCount: getResponseRowCount(response.data),
            status: response.status,
          };

          callbacks.onQueryCaptured(record);
        }
      }
      return response;
    },
    (error: AxiosError) => {
      const config = error.config;
      const requestId = config ? requestIds.get(config) : undefined;

      if (requestId && config) {
        const timing = requestTimings.get(requestId);
        requestIds.delete(config);
        requestTimings.delete(requestId);
        if (!timing || !timing.query) return Promise.reject(error);

        const duration = Math.round(performance.now() - timing.performanceStartTime);

        if (callbacks.getIsRecording() && callbacks.getCurrentQueriesCount() < callbacks.getMaxQueries()) {
          const record: CubeQueryRecord = {
            id: requestId,
            timestamp: timing.startTime,
            componentName: timing.componentName,
            pagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
            query: timing.query,
            queryKey: timing.queryKey || [],
            url: getFullUrl(config),
            method: getHttpMethod(config.method),
            duration,
            status: error.response?.status,
            error: error.message || 'Request failed',
          };

          callbacks.onQueryCaptured(record);
        }
      }
      return Promise.reject(error);
    },
  );
};

export const removeAxiosInterceptor = (): void => {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
  requestTimings.clear();
};

export const clearRequestTimings = (): void => {
  requestTimings.clear();
};
